import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type {
  DisplayDiffItem,
  InvoiceOrBillKind,
} from "./invoiceBillDisplayDiff.types";
import { getHeaderAllowlist, getLineAllowlist } from "./invoiceBillAllowlists";
import type {
  InvoiceBillHeaderLike,
  InvoiceBillLineLike,
} from "./invoiceBillDisplayValues";
import {
  getDisplayValueForHeaderField,
  getDisplayValueForLineField,
} from "./invoiceBillDisplayValues";
import { VALIDATION_OWNED_LINE_FIELD_KEYS } from "./validationSnapshot";

export interface BuildInvoiceBillDisplayDiffParams {
  kind: InvoiceOrBillKind;
  /** Document JSON before edits (invoice/bill header fields) */
  originalHeader: InvoiceBillHeaderLike | null | undefined;
  /** Document JSON after edits */
  newHeader: InvoiceBillHeaderLike | null | undefined;
  originalLineItems: InvoiceBillLineLike[] | null | undefined;
  newLineItems: InvoiceBillLineLike[] | null | undefined;
  /** GST line tax lookup when tax_name / tax_percentage are empty */
  taxes?: TaxEntity[];
  /**
   * When present (from validation completion), used as the "before" side for tax_total, total,
   * and line_items so system recalculations are not shown as user edits.
   */
  validationSnapshot?: {
    tax_total?: number;
    sub_total?: number;
    total?: number;
    line_items?: InvoiceBillLineLike[] | null;
  } | null;
  /**
   * When true, skip GST/TDS/derived line fields from the snapshot diff.
   * Those only appear via explicit `field_changes` (user edits after validation).
   * Leave false for create-preview summaries that list new document values.
   */
  omitValidationOwnedLineFields?: boolean;
}

function mergeValidationSnapshotIntoOriginalHeader(
  originalHeader: InvoiceBillHeaderLike | null | undefined,
  snapshot: BuildInvoiceBillDisplayDiffParams["validationSnapshot"],
): InvoiceBillHeaderLike | null | undefined {
  if (!snapshot) return originalHeader ?? undefined;
  const hasTaxTotal = snapshot.tax_total !== undefined;
  const hasSubTotal = snapshot.sub_total !== undefined;
  const hasTotal = snapshot.total !== undefined;
  if (!hasTaxTotal && !hasSubTotal && !hasTotal) {
    return originalHeader ?? undefined;
  }
  const base = originalHeader
    ? { ...originalHeader }
    : ({} as InvoiceBillHeaderLike);
  if (hasTaxTotal)
    (base as Record<string, unknown>).tax_total = snapshot.tax_total;
  if (hasSubTotal)
    (base as Record<string, unknown>).sub_total = snapshot.sub_total;
  if (hasTotal) (base as Record<string, unknown>).total = snapshot.total;
  return base;
}

function effectiveOriginalLineItemsForDiff(
  originalLineItems: InvoiceBillLineLike[] | null | undefined,
  snapshot: BuildInvoiceBillDisplayDiffParams["validationSnapshot"],
): InvoiceBillLineLike[] {
  if (snapshot?.line_items && Array.isArray(snapshot.line_items)) {
    return snapshot.line_items as InvoiceBillLineLike[];
  }
  return originalLineItems ?? [];
}

/** True when the reviewer explicitly saved exchange_rate from Basic Info. */
function isExchangeRateUserSet(
  header: InvoiceBillHeaderLike | null | undefined,
): boolean {
  return Boolean(
    (header as { exchange_rate_user_set?: unknown } | null | undefined)
      ?.exchange_rate_user_set,
  );
}

/** Same qty/rate on both sides — line totals may differ only from tax/TDS recalculation (validation). */
function lineQuantityAndRateEqual(
  a: InvoiceBillLineLike,
  b: InvoiceBillLineLike,
): boolean {
  const num = (v: unknown): number => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const n = parseFloat(String(v ?? "").trim());
    return Number.isFinite(n) ? n : 0;
  };
  return num(a.quantity) === num(b.quantity) && num(a.rate) === num(b.rate);
}

/**
 * Builds a list of display-only diff rows for allowlisted header + line fields.
 * Line items are aligned by **index** (0-based); missing rows use empty values.
 */
export function buildInvoiceBillDisplayDiff(
  params: BuildInvoiceBillDisplayDiffParams,
): DisplayDiffItem[] {
  const {
    kind,
    originalHeader,
    newHeader,
    originalLineItems,
    newLineItems,
    taxes,
    validationSnapshot,
    omitValidationOwnedLineFields = false,
  } = params;

  const items: DisplayDiffItem[] = [];
  const headerFields = getHeaderAllowlist(kind);
  const lineFields = getLineAllowlist(kind);

  const effectiveOldHeader = mergeValidationSnapshotIntoOriginalHeader(
    originalHeader,
    validationSnapshot,
  );

  const origLines = effectiveOriginalLineItemsForDiff(
    originalLineItems,
    validationSnapshot,
  );
  const nextLines = newLineItems ?? [];

  for (const { key, label } of headerFields) {
    // Zoho auto-fetched FX is system-owned (exchange_rate_user_set !== true).
    // Same idea as validationSnapshot for tax/totals: do not surface it as a
    // user edit in the approve / review changes modal.
    if (key === "exchange_rate" && !isExchangeRateUserSet(newHeader)) {
      continue;
    }
    // GST Total is recalculated by validation / line tax mapping — not a direct
    // user header edit. Line-level tax edits still appear via field_changes.
    if (omitValidationOwnedLineFields && key === "tax_total") {
      continue;
    }

    const oldText = getDisplayValueForHeaderField(
      kind,
      key,
      effectiveOldHeader,
    );
    const newText = getDisplayValueForHeaderField(kind, key, newHeader);
    if (oldText !== newText) {
      items.push({
        scope: "header",
        fieldKey: key,
        label,
        oldText,
        newText,
      });
    }
  }

  const maxLines = Math.max(origLines.length, nextLines.length);
  for (let i = 0; i < maxLines; i++) {
    const oldLine: InvoiceBillLineLike =
      (origLines[i] as InvoiceBillLineLike) ?? {};
    const newLine: InvoiceBillLineLike =
      (nextLines[i] as InvoiceBillLineLike) ?? {};

    for (const { key, label } of lineFields) {
      if (
        omitValidationOwnedLineFields &&
        VALIDATION_OWNED_LINE_FIELD_KEYS.has(key)
      ) {
        // GST / TDS / derived totals from handleValidationComplete — only surface
        // via explicit field_changes when the user edits after validation.
        continue;
      }
      if (
        key === "item_total_inclusive_of_tax" &&
        lineQuantityAndRateEqual(oldLine, newLine)
      ) {
        // Do not show derived line totals when only tax/TDS/validation changed qty×rate base.
        continue;
      }
      const oldText = getDisplayValueForLineField(kind, key, oldLine, taxes);
      const newText = getDisplayValueForLineField(kind, key, newLine, taxes);
      if (oldText !== newText) {
        items.push({
          scope: "line",
          lineNumber: i + 1,
          fieldKey: key,
          label,
          oldText,
          newText,
        });
      }
    }
  }

  return items;
}

/**
 * One line per non-empty new value for manual create preview (no "before" baseline).
 */
export function formatInvoiceBillAllowlistedCreateSummaryLines(
  kind: InvoiceOrBillKind,
  rawJson: Record<string, unknown> | null | undefined,
  taxes?: TaxEntity[],
): string[] {
  if (!rawJson) return [];
  const lineItems =
    (rawJson.line_items as InvoiceBillLineLike[] | undefined) ?? [];
  const items = buildInvoiceBillDisplayDiff({
    kind,
    originalHeader: {},
    newHeader: rawJson,
    originalLineItems: [],
    newLineItems: lineItems,
    taxes,
  });
  return items
    .filter((i) => String(i.newText).trim() !== "")
    .map((i) =>
      i.scope === "line" && i.lineNumber != null
        ? `Line ${i.lineNumber} — ${i.label}: ${i.newText}`
        : `${i.label}: ${i.newText}`,
    );
}
